/**
 * visibility.ts — the seller form's conditional-visibility evaluator (PIM Wave
 * P2, Task 2).
 *
 * A one-level, fail-hidden mirror of the backend's `_binding_is_visible`
 * (`apps/api/garden/features/catalog_schema/service.py`) — the SAME evaluator
 * the P0 submit gate and completeness scorer use, and a direct port of the
 * admin `FormPreview.isFieldVisible` (`features/admin/category-bindings/
 * components/FormPreview.tsx`), adapted to the seller form's tri-state boolean
 * encoding (`BOOL_TRUE`/`BOOL_FALSE`/`BOOL_UNANSWERED`).
 *
 * The law (verbatim from the backend, mirrored here):
 *   - A field with NO `visibility_rule` is always visible.
 *   - A rule-bearing field is visible IFF, all of:
 *       1. its controller (`rule.field_slug`) resolves to some field in this
 *          schema whose type is `select` or `boolean` — the only two
 *          "simulatable" controller types; anything else (including an
 *          unresolved slug) is FAIL-HIDDEN;
 *       2. that controller is currently ANSWERED (a `select` with a non-empty
 *          value_slug, or a `boolean` set to true/false — never "unanswered");
 *          an unanswered controller hides the dependent unconditionally,
 *          regardless of the rule's operator/value (backend:
 *          `values_by_field_id.get(controller.id) is None -> return False`);
 *       3. the operator matches — `eq`: the answer strictly `===` the rule's
 *          value; `in`: the rule's value is an array that includes the answer.
 *     Any other outcome is HIDDEN (never a thrown error, never a lax fallback).
 *
 * "One-level" (not recursive): a dependent is evaluated purely against its
 * controller's CURRENT value, never against whether that controller is itself
 * visible. The cascade (a hidden controller hiding its dependents) is achieved
 * upstream — `DynamicAttributeForm` (Task 4) clears a hidden field's value, so
 * its controller answer becomes unanswered and clause 2 hides the dependent —
 * exactly as the backend achieves it by not storing a hidden field's row.
 */
import {
  BOOL_FALSE,
  BOOL_TRUE,
  flattenFields,
  isPresent,
  type AttributeFormValues,
  type FieldType,
  type FormField,
  type FormSchemaResponse,
} from "@/features/seller-dashboard/attributes/formSchema";

/** A controller's resolved answer for rule comparison — `undefined` = unanswered. */
export type ControllerAnswer = string | boolean;

/** slug → field_type for every field in the schema (global controller lookup —
 * controllers resolve across ALL groups, not just the dependent's own section,
 * per the P0 law). */
export function buildControllerTypeMap(
  schema: FormSchemaResponse,
): ReadonlyMap<string, FieldType> {
  const map = new Map<string, FieldType>();
  for (const field of flattenFields(schema)) {
    map.set(field.field_slug, field.field_type);
  }
  return map;
}

/**
 * Resolve a controller's current answer from the flat form values, honoring
 * the seller encoding. `select` → its non-empty value_slug (else unanswered);
 * `boolean` → the real boolean for `"true"`/`"false"` (else unanswered — an
 * untouched or explicitly-cleared boolean is NOT "answered false"). Any other
 * controller type is non-simulatable → unanswered.
 */
function resolveControllerAnswer(
  controllerType: FieldType,
  raw: string | undefined,
): ControllerAnswer | undefined {
  if (controllerType === "select") {
    return isPresent(raw) ? (raw as string) : undefined;
  }
  if (controllerType === "boolean") {
    if (raw === BOOL_TRUE) return true;
    if (raw === BOOL_FALSE) return false;
    return undefined;
  }
  return undefined;
}

/**
 * One-level, fail-hidden visibility for a single field (see file header).
 */
export function isFieldVisible(
  field: FormField,
  controllerTypeBySlug: ReadonlyMap<string, FieldType>,
  values: AttributeFormValues,
): boolean {
  const rule = field.visibility_rule;
  if (!rule) return true;

  const controllerType = controllerTypeBySlug.get(rule.field_slug);
  if (controllerType !== "select" && controllerType !== "boolean") return false;

  const answer = resolveControllerAnswer(controllerType, values[rule.field_slug]);
  if (answer === undefined) return false; // unanswered controller → hidden

  if (rule.operator === "eq") return answer === rule.value;
  if (rule.operator === "in") {
    return Array.isArray(rule.value) && rule.value.includes(answer);
  }
  return false;
}

/**
 * The set of field slugs currently visible given `values` — the single source
 * both validation-error display and the write payload consult (hidden fields
 * are excluded from both).
 */
export function visibleFieldSlugs(
  schema: FormSchemaResponse,
  values: AttributeFormValues,
): Set<string> {
  const controllerTypeBySlug = buildControllerTypeMap(schema);
  const visible = new Set<string>();
  for (const field of flattenFields(schema)) {
    if (isFieldVisible(field, controllerTypeBySlug, values)) {
      visible.add(field.field_slug);
    }
  }
  return visible;
}

/** Count of rule-bearing fields currently hidden (for the "N fields hidden by
 * your answers" hint). */
export function hiddenFieldCount(
  schema: FormSchemaResponse,
  values: AttributeFormValues,
): number {
  const controllerTypeBySlug = buildControllerTypeMap(schema);
  let hidden = 0;
  for (const field of flattenFields(schema)) {
    if (field.visibility_rule && !isFieldVisible(field, controllerTypeBySlug, values)) {
      hidden += 1;
    }
  }
  return hidden;
}
